import type { Card, CardFilters, HiddenRarity, UserFilters } from '@/types'

type CardPredicate = (card: Card) => boolean

/**
 * The filter pipeline is a list of predicates built once per filterCards call:
 * each active filter contributes one predicate closing over its own
 * pre-computed lookup (Set/array), and a card passes when every predicate
 * passes. Inactive filters contribute nothing, so the per-card work scales
 * with the number of ACTIVE filters.
 */

/** Hidden cards / sets / (set, rarity) pairs — only when hiding is on. */
export function buildHiddenPredicates(
  userFilters: UserFilters | undefined,
  showHidden: boolean | undefined,
): CardPredicate[] {
  if (showHidden || !userFilters) return []

  const hiddenCardIds = new Set(userFilters.hiddenCards)
  const hiddenSetCodes = new Set(userFilters.hiddenSets)
  const predicates: CardPredicate[] = [
    (c) => !hiddenCardIds.has(c.id),
    (c) => !(c.setCode && hiddenSetCodes.has(c.setCode)),
  ]

  if (userFilters.hiddenRarities?.length) {
    const hiddenRarityKeys = new Set(userFilters.hiddenRarities.map((hr: HiddenRarity) => `${hr.setCode}|${hr.rarity}`))
    predicates.push((c) => !(c.setCode && c.rarity && hiddenRarityKeys.has(`${c.setCode}|${c.rarity}`)))
  }
  return predicates
}

/** Matches a card's `;`-joined multi-value field ("Red;Blue") if any selected value appears. */
function anySubstringPredicate(
  selected: string[] | undefined,
  field: (c: Card) => string | null | undefined,
): CardPredicate | null {
  if (!selected?.length) return null
  return (c) => {
    const value = field(c)
    return value != null && selected.some((s) => value.includes(s))
  }
}

/** Matches when `test` holds against a Set built once from the selected values. */
function membershipPredicate<T>(
  selected: T[] | undefined,
  test: (c: Card, chosen: Set<T>) => boolean,
): CardPredicate | null {
  if (!selected?.length) return null
  const chosen = new Set(selected)
  return (c) => test(c, chosen)
}

/** Discrete / multi-value selections (color, type, rarity, variant, …). */
export function buildSelectionPredicates(filters: CardFilters): CardPredicate[] {
  const candidates = [
    anySubstringPredicate(filters.color, (c) => c.color),
    membershipPredicate(filters.cardType, (c, chosen) => c.cardType != null && chosen.has(c.cardType)),
    membershipPredicate(filters.rarity, (c, chosen) => c.rarity != null && chosen.has(c.rarity)),
    membershipPredicate(filters.variantType, (c, chosen) => chosen.has(c.variantType)),
    membershipPredicate(filters.setName, (c, chosen) => c.setName != null && chosen.has(c.setName)),
    anySubstringPredicate(filters.attribute, (c) => c.attribute),
    membershipPredicate(filters.subTypes, (c, chosen) => c.subTypes?.some((s) => chosen.has(s)) ?? false),
    membershipPredicate(filters.counter, (c, chosen) => c.counter != null && chosen.has(c.counter)),
  ]
  return candidates.filter((p): p is CardPredicate => p !== null)
}

function matchesQuantityMode(quantity: number, mode: NonNullable<CardFilters['quantity']>): boolean {
  switch (mode) {
    case 'missing':
      return quantity === 0
    case 'incomplete':
      return quantity > 0 && quantity < 4
    case 'playset':
      return quantity === 4
    case 'duplicates':
      return quantity > 4
    default:
      return true
  }
}

interface RangeSpec {
  bound: number | null | undefined
  value: (c: Card) => number | null | undefined
  kind: 'min' | 'max'
}

/** Numeric min/max bounds on cost, power and market price. */
function buildNumericRangePredicates(filters: CardFilters): CardPredicate[] {
  const ranges: RangeSpec[] = [
    { bound: filters.costMin, value: (c) => c.cost, kind: 'min' },
    { bound: filters.costMax, value: (c) => c.cost, kind: 'max' },
    { bound: filters.powerMin, value: (c) => c.power, kind: 'min' },
    { bound: filters.powerMax, value: (c) => c.power, kind: 'max' },
    { bound: filters.priceMin, value: (c) => c.marketPrice, kind: 'min' },
    { bound: filters.priceMax, value: (c) => c.marketPrice, kind: 'max' },
  ]
  const predicates: CardPredicate[] = []
  for (const { bound, value, kind } of ranges) {
    if (bound == null) continue
    predicates.push((c) => {
      const v = value(c)
      return v != null && (kind === 'min' ? v >= bound : v <= bound)
    })
  }
  return predicates
}

/** Collection ownership / quantity modes — need the user's per-card quantities. */
function buildCollectionPredicates(
  filters: CardFilters,
  collectionQuantities: Record<number, number> | undefined,
): CardPredicate[] {
  if (!collectionQuantities) return []

  const predicates: CardPredicate[] = []
  if (filters.ownership) {
    const wantOwned = filters.ownership === 'owned'
    predicates.push((c) => (collectionQuantities[c.id] || 0) > 0 === wantOwned)
  }
  if (filters.quantity) {
    const mode = filters.quantity
    predicates.push((c) => matchesQuantityMode(collectionQuantities[c.id] || 0, mode))
  }
  return predicates
}

/** Trigger marker, numeric ranges, and collection ownership/quantity modes. */
export function buildRangePredicates(
  filters: CardFilters,
  collectionQuantities?: Record<number, number>,
): CardPredicate[] {
  const predicates: CardPredicate[] = []

  if (filters.trigger) {
    // Inferred from cardText — OPTCG uses a "[Trigger]" marker
    const want = filters.trigger
    predicates.push((c) => {
      const hasTrigger = c.cardText != null && /\[Trigger\]/i.test(c.cardText)
      return want === 'has' ? hasTrigger : !hasTrigger
    })
  }

  return [
    ...predicates,
    ...buildNumericRangePredicates(filters),
    ...buildCollectionPredicates(filters, collectionQuantities),
  ]
}
